/**
 * Boli Live - Product Detail Page
 *
 * eBay-style product page for fixed-price listings.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronLeft,
  Heart,
  Share2,
  ShoppingCart,
  Star,
  ShieldCheck,
  MapPin,
  Truck,
  ChevronRight,
  Loader2,
  Check,
} from 'lucide-react';
import { getProductService } from '@boli/core';
import type { Product } from '@boli/types';
import { cn, formatPKR } from '../lib/utils';
import { QuantitySelector } from '../components/QuantitySelector';
import { useCartStore } from '../lib/stores/cart-store';

// Simple localStorage-based wishlist for products
const WISHLIST_KEY = 'boli_product_wishlist';

function getWishlist(): string[] {
  try {
    const stored = localStorage.getItem(WISHLIST_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveWishlist(wishlist: string[]) {
  localStorage.setItem(WISHLIST_KEY, JSON.stringify(wishlist));
}

export default function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isWished, setIsWished] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const { addItem, openDrawer } = useCartStore();

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;

      setIsLoading(true);
      setError(null);

      try {
        const productService = getProductService();
        const fetchedProduct = await productService.getProduct(id);

        if (!fetchedProduct) {
          setError('Product not found');
          setIsLoading(false);
          return;
        }

        setProduct(fetchedProduct);
        setIsWished(getWishlist().includes(fetchedProduct.id));
      } catch {
        setError('Failed to load product');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const toggleWishlist = () => {
    if (!product) return;

    const wishlist = getWishlist();
    let newWishlist: string[];

    if (isWished) {
      newWishlist = wishlist.filter((itemId) => itemId !== product.id);
    } else {
      newWishlist = [...wishlist, product.id];
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 300);
    }

    saveWishlist(newWishlist);
    setIsWished(!isWished);
  };

  const handleShare = async () => {
    if (navigator.share && product) {
      try {
        await navigator.share({
          title: product.title,
          text: `Check out ${product.title} on Boli Live!`,
          url: window.location.href,
        });
      } catch {
        // Share failed silently
      }
    }
  };

  const handleAddToCart = async () => {
    if (!product || isAddingToCart || product.stock === 0) return;

    setIsAddingToCart(true);
    const success = await addItem(product.id, quantity);
    setIsAddingToCart(false);

    if (success) {
      setJustAdded(true);
      setTimeout(() => {
        setJustAdded(false);
        openDrawer();
      }, 500);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="sticky top-0 z-20 bg-background border-b border-border p-4 flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-full hover:bg-secondary transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div className="flex-1 h-5 bg-muted rounded animate-pulse mx-4" />
        </div>
        <div className="animate-pulse">
          <div className="aspect-square bg-muted" />
          <div className="p-4 space-y-4">
            <div className="h-6 bg-muted rounded w-3/4" />
            <div className="h-4 bg-muted rounded w-1/2" />
            <div className="h-8 bg-muted rounded w-1/3" />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !product) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <div className="text-6xl mb-4">404</div>
        <h1 className="text-xl font-bold text-foreground mb-2">Product Not Found</h1>
        <p className="text-muted-foreground text-center mb-4">
          {error || "The product you're looking for doesn't exist."}
        </p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
        >
          Go Home
        </button>
      </div>
    );
  }

  const hasDiscount = product.compareAtPrice && product.compareAtPrice > product.price;
  const discountPercentage = hasDiscount
    ? Math.round(((product.compareAtPrice! - product.price) / product.compareAtPrice!) * 100)
    : 0;

  const isOutOfStock = product.stock === 0;
  const images = product.images?.length > 0 ? product.images : ['/placeholder-product.jpg'];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-background pb-24"
    >
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-full hover:bg-secondary transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <h1 className="flex-1 text-sm font-medium text-foreground truncate mx-4">
            {product.title}
          </h1>

          <div className="flex items-center gap-1">
            <button
              onClick={toggleWishlist}
              className={cn(
                'p-2 rounded-full transition-colors',
                isWished ? 'text-red-500' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Heart
                className={cn(
                  'w-5 h-5 transition-transform',
                  isWished && 'fill-current',
                  isAnimating && 'scale-125'
                )}
              />
            </button>
            <button
              onClick={handleShare}
              className="p-2 rounded-full text-muted-foreground hover:text-foreground transition-colors"
            >
              <Share2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Image Gallery */}
      <div className="relative">
        <div className="aspect-square overflow-hidden bg-secondary">
          <img
            src={images[currentImageIndex]}
            alt={product.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Image Pagination Dots */}
        {images.length > 1 && (
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-1.5">
            {images.map((_: string, index: number) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={cn(
                  'w-2 h-2 rounded-full transition-colors',
                  index === currentImageIndex ? 'bg-primary' : 'bg-white/60'
                )}
              />
            ))}
          </div>
        )}

        {/* Discount Badge */}
        {hasDiscount && (
          <div className="absolute top-4 left-4">
            <span className="bg-red-600 text-white text-sm font-bold px-2 py-1 rounded">
              {discountPercentage}% OFF
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Title */}
        <h1 className="text-xl font-bold text-foreground">{product.title}</h1>

        {/* Rating (placeholder for now) */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5">
            {Array.from({ length: 5 }).map((_: unknown, i: number) => (
              <Star
                key={i}
                className={cn(
                  'w-4 h-4',
                  i < 4 ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'
                )}
              />
            ))}
          </div>
          <span className="text-sm text-muted-foreground">(123 reviews)</span>
        </div>

        {/* Price */}
        <div className="space-y-1">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-foreground font-display">
              {formatPKR(product.price)}
            </span>
            {hasDiscount && (
              <span className="text-lg text-muted-foreground line-through">
                {formatPKR(product.compareAtPrice!)}
              </span>
            )}
          </div>
          {hasDiscount && (
            <p className="text-sm text-green-600 font-medium">
              You save {formatPKR(product.compareAtPrice! - product.price)} ({discountPercentage}% off)
            </p>
          )}
        </div>

        {/* Quantity Selector */}
        {!isOutOfStock && (
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-foreground">Quantity:</span>
            <QuantitySelector
              value={quantity}
              min={1}
              max={product.stock}
              onChange={setQuantity}
            />
            <span className="text-sm text-muted-foreground">
              {product.stock} available
            </span>
          </div>
        )}

        {/* Stock Status */}
        {isOutOfStock && (
          <div className="bg-red-50 text-red-700 px-3 py-2 rounded-md text-sm font-medium">
            This item is currently out of stock
          </div>
        )}

        {/* Add to Cart Button */}
        <button
          onClick={handleAddToCart}
          disabled={isOutOfStock || isAddingToCart}
          className={cn(
            'w-full py-3.5 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all',
            isOutOfStock
              ? 'bg-muted text-muted-foreground cursor-not-allowed'
              : justAdded
              ? 'bg-green-600 text-white'
              : 'bg-primary text-primary-foreground hover:bg-primary/90'
          )}
        >
          {isOutOfStock ? (
            'Out of Stock'
          ) : isAddingToCart ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Adding to Cart...
            </>
          ) : justAdded ? (
            <>
              <Check className="w-5 h-5" />
              Added to Cart!
            </>
          ) : (
            <>
              <ShoppingCart className="w-5 h-5" />
              Add to Cart
            </>
          )}
        </button>

        {/* Divider */}
        <hr className="border-border" />

        {/* Description */}
        <div>
          <h2 className="font-semibold text-foreground mb-2">Description</h2>
          <p className="text-sm text-muted-foreground whitespace-pre-line">
            {product.description || 'No description available.'}
          </p>
        </div>

        {/* Divider */}
        <hr className="border-border" />

        {/* Shipping Info */}
        <div>
          <h2 className="font-semibold text-foreground mb-3">Shipping</h2>
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2 text-muted-foreground">
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                Ships from: {product.shippingInfo?.originCity || 'Pakistan'},{' '}
                {product.shippingInfo?.originProvince || ''}
              </span>
            </div>
            <div className="flex items-start gap-2 text-muted-foreground">
              <Truck className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                Estimated delivery: {product.shippingInfo?.estimatedDelivery || '3-5 days'}
              </span>
            </div>
            {product.shippingInfo?.shippingCost !== undefined && (
              <div className="text-foreground font-medium">
                Shipping: {product.shippingInfo.shippingCost === 0 ? 'Free' : formatPKR(product.shippingInfo.shippingCost)}
              </div>
            )}
          </div>
        </div>

        {/* Divider */}
        <hr className="border-border" />

        {/* Seller Info Card */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-bold text-foreground text-sm uppercase tracking-wider text-muted-foreground mb-3">
            Sold By
          </h3>

          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              {/* Avatar / Placeholder */}
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-secondary-foreground font-bold text-lg uppercase border border-border">
                S
              </div>

              <div>
                <div className="flex items-center gap-1">
                  <span className="font-bold text-foreground">Seller</span>
                  <ShieldCheck className="w-4 h-4 text-blue-500" />
                </div>

                <div className="flex items-center gap-1 text-xs mt-0.5">
                  <span className="text-green-600 font-medium">98% positive</span>
                </div>
              </div>
            </div>

            <span
              className="text-primary text-sm font-medium flex items-center opacity-50 cursor-not-allowed"
              title="Store page coming soon"
            >
              Visit Store <ChevronRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
